import * as ko from "knockout";
import { Route, system, WindowFormat } from "portalframework";
import { BaseService } from "./_common/_baseService";
import { httpErrorHandler } from "services";
import { portalHttpService } from "./_common/httpService";
import labels from "app/labels";
import * as toastr from "toastr";
import { UPLOAD_STATUS, attachment_wrapper, attachment } from "organized";
import * as entities from "entities";
import { USER_LANGUAGE_KEY } from "app/app-constants";

export class AttachmentResultDTO {
  name: string;
  type: string;
  content: string;
}

export enum UploadType {
  REQUEST,
  TASK,
}

const isMobile = (): boolean => {
  return system.windowFormat() === WindowFormat.Mobile || system.windowFormat() === WindowFormat.Phone;
};

export const downloadAttachmentFromBase64 = (attachment: AttachmentResultDTO) => {
  const linkSource = `data:${attachment.type};base64,${attachment.content}`;
  const downloadLink = document.createElement("a");
  const fileName = attachment.name;
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};

class AttachmentService extends BaseService {
  constructor() {
    super("attachment");
  }

  public getSolutionAttachment = async (solution_id: string, attachment_id: string | number): Promise<void> => {
    const urlResult = new URL(`${this.baseApiUrl()}/GetAttachment`);
    urlResult.searchParams.append("request_id", "");
    urlResult.searchParams.append("task_id", "");
    urlResult.searchParams.append("attachment_id", `${attachment_id}`);
    urlResult.searchParams.append("solution_id", solution_id || "");

    const attachmentResult: AttachmentResultDTO = await portalHttpService.get(urlResult.href);

    downloadAttachmentFromBase64(attachmentResult);
  };

  public getAttachment = async (request_id: string, task_id: string, attachment_id: string | number): Promise<void> => {
    let urlResult = new URL(`${this.baseApiUrl()}/GetAttachment`);

    if (isMobile()) {
      // iOS Safari specific handling if needed
      urlResult = new URL(`${this.baseApiUrl()}/DownloadAttachment`);
    }

    urlResult.searchParams.append("solution_id", "");
    urlResult.searchParams.append("request_id", request_id || "");
    urlResult.searchParams.append("task_id", task_id || "");
    urlResult.searchParams.append("attachment_id", `${attachment_id}`);

    if (!isMobile()) {
      const attachmentResult: AttachmentResultDTO = await portalHttpService.get(urlResult.href);

      downloadAttachmentFromBase64(attachmentResult);
      return;
    }

    const link = document.createElement("a");
    link.href = urlResult.href;
    link.download = attachment_id.toString();

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  public SaveAttachment = async (entity_id: string, attachment, uploadType: UploadType): Promise<any> => {
    var formData = new FormData();
    try {
      switch (uploadType) {
        case UploadType.REQUEST: {
          formData.append("request_id", entity_id);
          formData.append("task_or_request", "request");
          break;
        }
        case UploadType.TASK: {
          formData.append("task_id", entity_id);
          formData.append("task_or_request", "task");
          break;
        }
      }
      formData.append("imageData", this.dataURLtoBlob(attachment.attachment.file.filedata));
      formData.append("attachment_name", attachment.attachment.attachment_name);
      formData.append("attachment_type", attachment.attachment.attachment_type);
      formData.append("file_type", attachment.attachment.file_type);
      formData.append("attachment_description", attachment.attachment.attachment_description);

      return await $.ajax({
        type: "POST",
        url: `${this.baseApiUrl()}/Upload`,
        data: formData,
        contentType: false,
        processData: false,
      });
    } catch (error) {
      console.error(error);
      let errorProcessed = httpErrorHandler.processError(error);
      let message = errorProcessed.message;
      message = labels.descr(message);

      toastr.error(
        `${labels.descr("cc_P_ERROR_WHILE_UPLOADING_ATTACHMENT")} <strong>${
          attachment.attachment.attachment_name
        }</strong>: ${message}`,
      );
      throw error;
    }
  };

  public SaveAttachments = async (attachments: attachment_wrapper[], request_id: string, uploadType: UploadType) => {
    const promises = [];
    attachments.forEach((at) => {
      at.attachment().uploadStatus(UPLOAD_STATUS.UPLOADING);
      promises.push(
        attachmentService
          .SaveAttachment(request_id, ko.toJS(at), uploadType)
          .then((result) => {
            const attachmentId = result?.request_attachment?.attachment_id;
            const attachmentName = result?.request_attachment?.attachment?.attachment_name;
            at.attachment_id(attachmentId);
            at.attachment().attachment_id(attachmentId);
            if (attachmentName) {
              at.attachment().attachment_name(attachmentName);
            }
            at.attachment().uploadStatus(UPLOAD_STATUS.COMPLETE);

            setTimeout(() => {
              at.attachment().uploadStatus(UPLOAD_STATUS.UPLOADED);
            }, 5000);
          })
          .catch((error) => {
            at.attachment().uploadStatus(UPLOAD_STATUS.ERROR);
            throw error;
          }),
      );
    });

    var promiseWrapped: any = Promise;

    const result: any[] = await promiseWrapped.allSettled(promises);
    const failedUploads = result.filter((u) => u.status === "rejected");
    return failedUploads.length === 0;
  };

  public getAttachmentWithLoader = async (attachment: attachment, request_id: string, task_id: string) => {
    if (attachment.attachment_id()) {
      try {
        attachment.uploadStatus(UPLOAD_STATUS.UPLOADING);
        await attachmentService.getAttachment(request_id, task_id, attachment.attachment_id());
        attachment.uploadStatus(UPLOAD_STATUS.COMPLETE);
      } catch (error) {
        toastr.error(error.message);
        console.error(error.message);
        attachment.uploadStatus(UPLOAD_STATUS.ERROR);
      } finally {
        setTimeout(() => {
          attachment.uploadStatus(UPLOAD_STATUS.UPLOADED);
        }, 1500);
      }
    }
  };

  public creatRequestAttachment = (request_id, attachment) => {
    var request_attachment_data = {
      request_id: request_id,
      attachment: {
        file: attachment.file,
        attachment_name: attachment.name,
        attachment_type: attachment.attachment_type,
        attachment_description: attachment.description,
        file_type: attachment.contentType,
      },
    };

    return new attachment_wrapper(request_attachment_data);
  };

  public SaveSignature = async (
    blobImage: Blob,
    attachment: entities.attachment,
    main_table: string,
    main_table_field_value: string,
  ) => {
    var formData = new FormData();
    const locale = localStorage.getItem(USER_LANGUAGE_KEY);

    try {
      formData.append("imageData", blobImage);
      formData.append("main_table", main_table);
      formData.append("main_table_field_value", main_table_field_value);
      formData.append("file_type", attachment.file_type());
      formData.append("signer", attachment.signer());
      formData.append("attachment_name", attachment.attachment_name());
      formData.append("attachment_description", attachment.attachment_description());
      formData.append("locale", locale);

      return await $.ajax({
        type: "POST",
        url: `${this.baseApiUrl()}/SaveSignature`,
        data: formData,
        contentType: false,
        processData: false,
      });
    } catch (error) {
      console.error(error);
      let errorProcessed = httpErrorHandler.processError(error);
      let message = errorProcessed.message;
      message = labels.descr(message);

      toastr.error(
        `${labels.descr(
          "cc_P_ERROR_WHILE_UPLOADING_ATTACHMENT",
        )} <strong>${attachment.attachment_name()}</strong>: ${message}`,
      );
    }
  };

  private dataURLtoBlob = (dataurl: string) => {
    if (!(dataurl === "data:")) {
      var arr = dataurl.split(","),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    } else {
      throw new Error("cc_P_EMPTYFILE");
    }
  };
}

export const attachmentService = new AttachmentService();
